
import { FC, MouseEvent, ReactNode } from 'react';
import SpriteIcon from '../shared/sprite-icon';
import { useTranslation } from '../../hooks/use-translation';
import { AppDownload } from '../../interfaces/domain/ow-app.modal';
import OWLink from '../shared/ow-link';
import { useDownloadInProgress } from '../../hooks/use-download-in-progress';
import { useDownloadLabel } from '../../hooks/use-download-label';
import useIsOverwolfIframe from '../../hooks/use-is-overwolf-iframe';
import DownloadInProgressLabel from './download-in-progress-label';

interface MultiDownloadListProps {
  appTitle: string;
  dlData: AppDownload[];
  skipMain?: boolean;
  extraItem?: ReactNode;
  // The app's own id / type, used as fallbacks for a row that has no explicit
  // appId / isElectron (mirrors the click handler's `clickedAppId` resolution).
  mainAppId: string;
  itemIsElectron: boolean;
  handleClickDownload:
  (event: MouseEvent<HTMLElement>, overrideUrl: string) => void;
}

// -----------------------------------------------------------------------------
export const MultiDownloadList:
FC<MultiDownloadListProps> = (props): JSX.Element => {
  const {
    appTitle,
    dlData,
    skipMain = false,
    extraItem,
    mainAppId,
    itemIsElectron,
    handleClickDownload
  } = props;

  const { t } = useTranslation();
  const downloadInProgress = useDownloadInProgress();
  const getDownloadLabel = useDownloadLabel();
  const { isOverwolfIframe } = useIsOverwolfIframe();

  const downloads = dlData?.slice(skipMain ? 1 : 0) ?? [];

  return (
    <ul className='multi-options-list'>
      {downloads.map((dlItem, i) => {
        const appId = dlItem.appId ?? mainAppId;
        // While this specific download/install is in progress, animate its row
        // (so the non-main button that was clicked shows the progress).
        const isDownloading = downloadInProgress(appId);
        // Per-download label: only this row switches to "Launch" if its own app
        // is installed (Electron or native), e.g. "Download standalone" ->
        // "Launch standalone".
        const label = getDownloadLabel(dlItem, mainAppId, itemIsElectron);
        // Electron rows go through the shell's downloadApp (an installer file
        // download); native rows go through installApp — different verb while
        // in progress ("Downloading..." vs "Installing...").
        const isElectronItem = dlItem.isElectron ?? itemIsElectron;
        const showInstallingState = isDownloading && isOverwolfIframe;
        return (
          <li key={`${dlItem.platform}-${i}`} className='option-item'>
            <OWLink
              className={
                isDownloading && !isOverwolfIframe
                  ? 'option-item-url is-downloading'
                  : 'option-item-url'
              }
              href={dlItem.url}
              download={t('multiAppInstaller', {
                title: appTitle,
                platform: dlItem.platform,
              })}
              onClick={(event: MouseEvent<HTMLElement>) =>
                handleClickDownload(event, dlItem.url)
              }
            >
              {showInstallingState ? (
                <DownloadInProgressLabel isElectron={isElectronItem} />
              ) : (
                <>
                  <svg>
                    <SpriteIcon icon={dlItem.platform.toLowerCase()} />
                  </svg>
                  <span className='option-item-label'>{label}</span>
                </>
              )}
            </OWLink>
          </li>
        );
      })}
      {extraItem}
    </ul>
  );
};
